import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { JobContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import { JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES } from "@/app/config/contact-form-file-limits";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type JobContactFormFieldNameT =
  | "salutation"
  | "firstname"
  | "lastname"
  | "zipCode"
  | "city"
  | "email"
  | "phone"
  | "cv"
  | "certificates"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const jobContactFormDefaultValues: DefaultValues<JobContactFormDataT> = {
  salutation: undefined,
  firstname: "",
  lastname: "",
  zipCode: "",
  city: "",
  email: "",
  phone: "",
  cv: undefined,
  certificates: undefined,
  comments: undefined,
  privacy: false,
  mail: "",
  case: "CASE",
  URL: undefined,
  source: "Karriere Anfrage",
  dealerdesk: "Karriere",
};

const salutationField: ContactFormFieldConfigT<JobContactFormFieldNameT> = {
  name: "salutation",
  type: "select",
  label: "Anrede",
  placeholder: "Bitte auswählen...",
  colSpan: 2,
  options: [
    { value: "Herr", label: "Herr" },
    { value: "Frau", label: "Frau" },
    { value: "Divers", label: "Divers" },
  ],
};

const primaryFields: ContactFormFieldConfigT<JobContactFormFieldNameT>[] = [
  {
    name: "lastname",
    type: "name",
    label: "Name",
    placeholder: "Nachname",
  },
  {
    name: "firstname",
    type: "name",
    label: "Vorname",
    placeholder: "Vorname",
  },
  {
    name: "zipCode",
    type: "text",
    label: "PLZ",
    placeholder: "PLZ",
  },
  {
    name: "city",
    type: "text",
    label: "Ort",
    placeholder: "Ort",
  },
  {
    name: "email",
    type: "text",
    inputType: "email",
    label: "E-Mail",
    placeholder: "E-Mail",
  },
  {
    name: "phone",
    type: "phone",
    label: "Telefon",
    placeholder: "Telefonnummer",
  },
  {
    name: "cv",
    type: "file",
    label: "Lebenslauf",
    buttonLabel: "Lebenslauf hochladen",
    accept:
      ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*",
    maxSize: JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES,
    description: "Nur 1 Datei erlaubt",
  },
  {
    name: "certificates",
    type: "file",
    label: "Zeugnisse (optional)",
    buttonLabel: "Zeugnisse hochladen",
    accept:
      ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*",
    maxSize: JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES,
    description: "Nur 1 Datei erlaubt",
  },
  {
    name: "comments",
    type: "textarea",
    label: "Gibt es weitere Bemerkungen oder Hinweise?",
    placeholder: "Ihre Nachricht...",
    colSpan: 2,
  },
];

const privacyField: ContactFormFieldConfigT<JobContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="font-semibold underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const hiddenFields: ContactFormFieldConfigT<JobContactFormFieldNameT>[] = [
  {
    name: "mail",
    type: "honeypot",
  },
  {
    name: "case",
    type: "select",
    hidden: true,
    options: [{ value: "CASE", label: "CASE" }],
  },
  {
    name: "URL",
    type: "text",
    hidden: true,
  },
];

export const jobContactFormConfig: ContactFormConfigT<JobContactFormFieldNameT> =
  {
    sections: [
      {
        id: "application",
        title: "Online-Bewerbung",
        layout: "grid",
        columns: 2,
        fields: [
          salutationField,
          ...primaryFields,
          privacyField,
          ...hiddenFields,
        ],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Bewerbung abschicken",
    submittingLabel: "Wird abgeschickt...",
    successToast: {
      title: "Bewerbung erfolgreich gesendet",
      description: "Wir melden uns schnellstmöglich bei Ihnen.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
